/**
 * D-EZ-110 — THE STOCK CAR DISPATCH: PRESEASON PREVIEW.
 *
 * The edition the paper prints before a season turns a wheel: who the
 * favourites are on last year's form, what to watch (the rivalries carried
 * over, the rookie class, men coming back from injury, a veteran's possible
 * last ride, the new rides), and the defending champion's challenge.
 *
 * ⚠ IT KNOWS LAST SEASON AND THE WINTER, AND NOTHING ELSE. It reads the league
 * through season n − 1 and its winter (which is over before season n starts),
 * plus the roster the winter handed season n. Not one result of season n.
 * Proved in `chapters.lookahead.test.ts` (season n doctored; preview unchanged).
 *
 * ⚠ WRITTEN, NEVER GENERATED (Sept 12 ruling). This edition is assembled from
 * slots and templates; no model is called to write it, so it may appear on its
 * own. A model polish, if one is ever added, happens only on the explicit
 * action, like every other edition.
 */

import { MILESTONE_WINS } from '../history/index.ts';
import { stableHash } from '../story/hometown.ts';
import { STATE_WORDS } from '../people/rivalry.ts';
import type { DriverCard } from '../types.ts';
import { chapterContext, nameIn, seasonsThrough, teamBase, teamOfIn, type ChapterSeason } from './league.ts';
import { farewellWatch, listOf } from './farewell.ts';
import { rookieClass } from './rookies.ts';
import { sillySeason } from './winter.ts';
import { count, cycle, headlineNamer, ordinal, plural, tell, tellOne, winterQuote, type Said, type Told } from './text.ts';

export const DISPATCH = 'The Stock Car Dispatch';

export interface Favourite {
  driverId: string;
  name: string;
  team: string | null;
  tier: 'favourite' | 'contender' | 'dark horse';
  score: number;
  line: Told;
}

export interface PreviewSection {
  key: 'favourites' | 'champion' | 'rivalries' | 'rookies' | 'comebacks' | 'last-ride' | 'new-rides' | 'milestones';
  title: string;
  paragraphs: Told[];
  quotes: Said[];
}

export interface PreseasonPreview {
  seasonNumber: number;
  masthead: string;
  edition: Told;
  headline: Told;
  dek: Told;
  favourites: Favourite[];
  sections: PreviewSection[];
}

interface Form {
  rank: number;
  wins: number;
  top10: number;
  /** Average finish over his last ten starts of last season. */
  lastTen: number | null;
}

/** The preview for season `seasonNumber`. Null when there is no roster for it yet. */
export function preseasonPreview(input: readonly ChapterSeason[], seasonNumber: number): PreseasonPreview | null {
  const before = seasonsThrough(input, seasonNumber - 1, { winter: true });
  const last = before.find((s) => s.seasonNumber === seasonNumber - 1) ?? null;
  const live = input.find((s) => s.seasonNumber === seasonNumber) ?? null;
  const roster: readonly DriverCard[] | undefined = last?.offseason?.roster ?? (live ? live.roster : undefined);
  const teams = last?.offseason?.teams ?? live?.teams;
  if (!roster || roster.length === 0) return null;
  const cx = chapterContext(before, input);
  const used = new Set<string>();
  const K = (...p: (string | number)[]) => `preview|${cx.leagueKey}|${seasonNumber}|${p.join('|')}`;
  const onRoster = new Set(roster.map((c) => c.driverId));
  const teamName = (id: string) => teamOfIn(teams, id)?.name ?? null;
  const sections: PreviewSection[] = [];

  // ---- form ---------------------------------------------------------------
  const form = new Map<string, Form>();
  if (last) {
    const lines = new Map<string, number[]>();
    for (const r of last.season.races) for (const f of r.race.finishingOrder) lines.set(f.driverId, [...(lines.get(f.driverId) ?? []), f.place]);
    for (const d of last.season.finalStandings.drivers) {
      const l = (lines.get(d.driverId) ?? []).slice(-10);
      form.set(d.driverId, { rank: d.rank, wins: d.wins, top10: d.top10, lastTen: l.length >= 5 ? Math.round((l.reduce((t, x) => t + x, 0) / l.length) * 10) / 10 : null });
    }
  }
  const rerate = new Map((last?.offseason?.rerates ?? []).map((r) => [r.driverId, r]));
  const field = roster.length;
  const headName = headlineNamer(roster.map((c) => [c.driverId, c.name] as [string, string]));

  // ---- favourites -----------------------------------------------------------
  const scored = roster.map((c) => {
    const f = form.get(c.driverId);
    const r = rerate.get(c.driverId);
    const gasDelta = r ? r.after.gasPedal - r.before.gasPedal : 0;
    const paper = c.gasPedal * 6 + c.qualities.length * 4 + c.starred.length * 2 + c.escape;
    const score = f
      ? ((field - Math.min(f.rank, field)) / field) * 40 + f.wins * 4 + f.top10 * 0.5 + (f.lastTen !== null ? ((43 - f.lastTen) / 43) * 25 : 0) + gasDelta * 4
      : paper * (last ? 0.5 : 1);
    return { c, f, score, gasDelta };
  });
  scored.sort((a, b) => b.score - a.score || a.c.driverId.localeCompare(b.c.driverId));
  const favourites: Favourite[] = [];
  const top = scored.slice(0, 4);
  const darkPool = scored.slice(4).filter((x) => (x.f ? x.f.rank > 10 : true));
  const dark = darkPool[0];
  for (const [i, x] of [...top, ...(dark ? [dark] : [])].entries()) {
    const tier: Favourite['tier'] = i < 2 ? 'favourite' : i < 4 ? 'contender' : 'dark horse';
    const team = teamName(x.c.driverId);
    const slots: Record<string, string | number> = { name: x.c.name, sur: headName(x.c.driverId, x.c.name) };
    if (team) slots.team = team;
    let line: Told;
    if (x.f) {
      Object.assign(slots, { rank: ordinal(x.f.rank), w: count(x.f.wins), wl: plural(x.f.wins, 'win'), t10: count(x.f.top10) });
      if (x.f.lastTen !== null) slots.avg = x.f.lastTen;
      const opts =
        x.f.wins > 0
          ? ['{name} — {rank} last year with {w} {wl}{tail}', '{name}: {w} {wl} and {rank} in the championship last season{tail}']
          : ['{name} — {rank} last year, {t10} top tens, no wins{tail}', '{name}: {rank} in the standings and still looking for the win that turns it around{tail}'];
      const tail =
        x.gasDelta > 0
          ? `, and the card picked up speed over the winter.`
          : x.gasDelta < 0
            ? `, though the card lost a step over the winter.`
            : x.f.lastTen !== null
              ? `; averaged ${x.f.lastTen} over his last ten starts.`
              : '.';
      line = tellOne(K('fav', x.c.driverId), opts, { ...slots, tail });
    } else {
      line = tell('{name} — no league record yet; the card says {gas} on the gas pedal and {q} {ql}.', { ...slots, gas: x.c.gasPedal, q: count(x.c.qualities.length), ql: plural(x.c.qualities.length, 'quality', 'qualities') });
    }
    favourites.push({ driverId: x.c.driverId, name: x.c.name, team, tier, score: Math.round(x.score * 10) / 10, line });
  }
  sections.push({ key: 'favourites', title: last ? 'The favourites, on last year’s form' : 'The favourites, on paper', paragraphs: favourites.map((f) => f.line), quotes: [] });

  // ---- the defending champion --------------------------------------------
  const champId = last?.season.championId ?? null;
  let headline: Told;
  if (last && champId) {
    const champName = last.season.finalStandings.byDriver[champId]?.name ?? nameIn(before, champId);
    const e = cx.registry.byId[champId];
    const titles = e?.career.championships ?? 1;
    const paras: Told[] = [];
    const quotes: Said[] = [];
    const settled = before.filter((s) => s.complete !== false && s.season.championId);
    const repeats = settled.filter((s) => before.find((p) => p.seasonNumber === s.seasonNumber - 1)?.season.championId === s.season.championId);
    const streak = (() => {
      let k = 0;
      for (let n = seasonNumber - 1; n >= 1; n--) {
        if (before.find((s) => s.seasonNumber === n)?.season.championId === champId) k += 1;
        else break;
      }
      return k;
    })();
    const slots: Record<string, string | number> = { name: champName, sur: headName(champId, champName), titles: count(titles), t: plural(titles, 'title') };
    if (!onRoster.has(champId)) {
      const retired = (last.offseason?.retirements ?? []).some((r) => r.driverId === champId);
      paras.push(retired ? tell('There is no title defence: {name} retired over the winter, and the crown is up for grabs.', slots) : tell('There is no title defence: {name} is not on the entry list, and the crown is up for grabs.', slots));
      headline = cycle(`preview-h|${cx.leagueKey}|open`, seasonNumber, ['The crown is up for grabs', 'No champion on the grid', 'Wide open', 'A title with no holder', 'Somebody new will wear the crown'], slots);
    } else {
      const nowTeam = teamName(champId);
      const wasTeam = last.teams.find((t) => t.driverIds.includes(champId)) ?? null;
      if (nowTeam) slots.team = nowTeam;
      if (streak >= 2) paras.push(tell('{name} goes for a {nth} straight title, with {titles} {t} already on the shelf.', { ...slots, nth: ordinal(streak + 1) }));
      else if (repeats.length === 0) paras.push(tellOne(K('def0'), ['{name} starts the year as champion, with a target on his back — and history against him: nobody has defended a title in this league.', 'Nobody has won back-to-back titles here. {name} gets the first crack at it.'], slots));
      else paras.push(tell('{name} defends. It has been done before — {who} — but not often.', { ...slots, who: listOf([...new Set(repeats.map((s) => s.season.finalStandings.byDriver[s.season.championId as string]?.name ?? ''))].filter(Boolean)) }));
      if (wasTeam && nowTeam && teamBase(wasTeam.teamId) !== teamBase(teamOfIn(teams, champId)?.teamId ?? '')) paras.push(tell('And he defends it in new colors, with {team}.', slots));
      const champFav = favourites.findIndex((f) => f.driverId === champId);
      if (champFav === 0) paras.push(tell('On last year’s form he is still the man to beat.', {}));
      else if (favourites[0]) paras.push(tell('On last year’s form, though, the man to beat is {fav}.', { fav: favourites[0].name }));
      const rel = cx.people.book.rivalries.find((r) => (r.a === champId || r.b === champId) && onRoster.has(r.a === champId ? r.b : r.a) && r.state !== 'dormant' && r.state !== 'cooled');
      if (rel) {
        const other = rel.a === champId ? rel.bName : rel.aName;
        paras.push(tell('The man most likely to make it personal: {other}.', { other }));
      }
      const q = winterQuote('defending', champId, champName, cx.people, {}, `${seasonNumber}`, used);
      if (q) quotes.push(q);
      headline =
        champFav === 0
          ? cycle(`preview-h|${cx.leagueKey}|champ`, seasonNumber, ['{sur} the man to beat again', 'Everybody chasing {sur}', 'Can anybody stop {sur}?', 'Same target, same man: {sur}', '{sur} still the one they all have to catch'], slots)
          : favourites[0]
            ? cycle(`preview-h|${cx.leagueKey}|fav`, seasonNumber, ['{fav} tipped to dethrone {sur}', '{fav} the pick; {sur} out to prove it', '{sur} defends, {fav} favoured', 'Champion {sur}, but the form says {fav}', '{fav} comes for {sur}’s crown'], { ...slots, fav: headName(favourites[0].driverId, favourites[0].name) })
            : tell('{sur} defends', slots);
    }
    sections.push({ key: 'champion', title: 'The champion’s challenge', paragraphs: paras, quotes });
  } else {
    headline = favourites[0] ? tellOne(K('h-first'), ['A new league, and {sur} the pick on paper', 'Year one: {sur} the early favourite'], { sur: headName(favourites[0].driverId, favourites[0].name) }) : tell('A new league');
  }

  // ---- rivalries carried over --------------------------------------------
  if (before.length > 0) {
    const book = cx.people.book;
    const live = book.rivalries
      .filter((r) => onRoster.has(r.a) && onRoster.has(r.b) && (r.state === 'boiling' || r.state === 'hot' || r.state === 'simmering'))
      .slice(0, 3);
    if (live.length > 0) {
      const paras: Told[] = [];
      const quotes: Said[] = [];
      for (const r of live) {
        const slots: Record<string, string | number> = { a: r.aName, b: r.bName, state: (STATE_WORDS[r.state] ?? r.state).toLowerCase(), aa: r.headToHead.aAhead, bb: r.headToHead.bAhead, sa: headName(r.a, r.aName), sb: headName(r.b, r.bName) };
        if (r.worst) slots.worst = r.worst.text;
        paras.push(
          tellOne(K('riv', r.a, r.b), r.worst
            ? ['{a} and {b} ({state}) carry it into the new year. {worst}', 'Still {state} between {a} and {b} after the winter. {worst}']
            : ['{a} and {b}: still {state} after the winter.'], slots),
        );
        if (r.headToHead.races > 0) paras.push(tell('Head to head: {sa} ahead {aa} times, {sb} {bb}.', slots));
        const speaker = stableHash(K('riv-q', r.a, r.b)) % 2 === 0 ? [r.a, r.aName, r.bName] : [r.b, r.bName, r.aName];
        const q = winterQuote('rival-preseason', speaker[0], speaker[1], cx.people, { other: headName(speaker[0] === r.a ? r.b : r.a, speaker[2]) }, `${seasonNumber}`, used);
        if (q) quotes.push(q);
      }
      sections.push({ key: 'rivalries', title: 'Carried over the winter', paragraphs: paras, quotes: quotes.slice(0, 2) });
    }
  }

  // ---- the rookie class -----------------------------------------------------
  if (last) {
    const rc = rookieClass(input, seasonNumber - 1, cx);
    if (rc) {
      const pick = rc.reports[0];
      const paras: Told[] = [rc.intro];
      if (pick && rc.reports.length > 1) paras.push(tell('The pick of them on paper is {name}.', { name: pick.name }), pick.lead);
      const quotes = pick?.quote ? [pick.quote] : [];
      sections.push({ key: 'rookies', title: 'The rookie class', paragraphs: paras, quotes });
    }
  }

  // ---- back from injury -------------------------------------------------------
  if (last) {
    const hurt = new Map<string, { race: number; raceName: string }>();
    for (const r of last.season.races) {
      for (const ev of r.events ?? []) if (ev.momentTag === 'season.injury') for (const id of ev.actorIds) hurt.set(id, { race: r.raceNumber, raceName: r.scheduled.raceName });
    }
    const stillOut = new Map(last.offseason?.carry?.season.injured ?? []);
    const paras: Told[] = [];
    const quotes: Said[] = [];
    for (const [id, h] of hurt) {
      if (!onRoster.has(id)) continue;
      const name = nameIn(before, id);
      const out = stillOut.get(id);
      paras.push(
        out
          ? tell('{name} was badly hurt in the {race} last year and still has {k} {r} to sit out.', { name, race: h.raceName, k: count(out), r: plural(out, 'race') })
          : tellOne(K('back', id), ['{name} is back after being badly hurt in the {race} last season.', 'Cleared to race: {name}, seriously hurt in the {race} a year ago.'], { name, race: h.raceName }),
      );
      if (!out) {
        const q = winterQuote('comeback', id, name, cx.people, {}, `${seasonNumber}`, used);
        if (q) quotes.push(q);
      }
    }
    if (paras.length > 0) sections.push({ key: 'comebacks', title: 'Back from injury', paragraphs: paras.slice(0, 4), quotes: quotes.slice(0, 1) });
  }

  // ---- the veteran's possible last ride --------------------------------------
  const watch = farewellWatch(input, seasonNumber, { limit: 3, cx });
  if (watch.length > 0) {
    const paras: Told[] = [];
    const quotes: Said[] = [];
    for (const w of watch.slice(0, 2)) {
      paras.push(...w.lines);
      if (w.quote) quotes.push(w.quote);
    }
    if (watch.length > 2) paras.push(tell('Also in the frame: {list}.', { list: listOf(watch.slice(2).map((w) => `${w.name} (${w.age})`)) }));
    sections.push({ key: 'last-ride', title: 'The last ride?', paragraphs: paras, quotes: quotes.slice(0, 1) });
  }

  // ---- new rides -------------------------------------------------------------
  if (last) {
    const ss = sillySeason(input, seasonNumber - 1, cx);
    const moves = ss?.sections.find((x) => x.key === 'on-the-move')?.stories.filter((m) => m.kind === 'moved' || m.kind === 'moved-up') ?? [];
    if (moves.length > 0) {
      sections.push({
        key: 'new-rides',
        title: 'New rides',
        paragraphs: moves.slice(0, 3).map((m) => m.body[0]),
        quotes: moves.slice(0, 3).map((m) => m.quote).filter((q): q is Said => q !== null).slice(0, 1),
      });
    }
  }

  // ---- milestones in reach ------------------------------------------------------
  {
    const paras: Told[] = [];
    for (const c of roster) {
      const e = cx.registry.byId[c.driverId];
      if (!e) continue;
      const w = e.career.wins;
      const next = MILESTONE_WINS.find((m) => m > w);
      if (next && next - w <= 2 && w >= 4) paras.push(tell('{name} sits on {w} career wins; {m} is within reach.', { name: e.name, w, m: next }));
    }
    if (paras.length > 0) sections.push({ key: 'milestones', title: 'Numbers to watch', paragraphs: paras.slice(0, 4), quotes: [] });
  }

  const dek = favourites.length >= 3
    ? tell('The favourites, the feuds, the rookies and the questions, before the green flag drops on season {n}.', { n: seasonNumber })
    : tell('Everything to watch before season {n}.', { n: seasonNumber });
  return {
    seasonNumber,
    masthead: DISPATCH,
    edition: tell('Preseason preview · season {n}', { n: seasonNumber }),
    headline,
    dek,
    favourites,
    sections,
  };
}
